/**
 * Shared role-name constants for access gating.
 *
 * These are the title-cased *display* strings the backend builds from
 * `User.get_roles()` — the vocabulary `hasRole(user, [...])` / `RoleGuard`
 * match against (`user.roles`), NOT the snake_case `user.roleNames` used for
 * role assignment. See frontend/CLAUDE.md § Role-Based Access.
 *
 * Previously duplicated in `routeConfig.jsx` and `power/HomepageCard.jsx`; they
 * now both import from here so a route guard and its homepage tile can never
 * disagree about who may see a feature.
 */
export const SITE_ADMIN_ROLES = ['Site Admin Role']

export const PARTNER_ADMIN_ROLES = ['Partner Admin Role']

export const ADMIN_ROLES = ['Admin Role', 'Site Admin Role']

// Org admin pages the backend also opens to partner admins, who administer the orgs
// in their own portfolio: every page the setup hub and onboarding wizard send them
// to (Organisation settings, External services, Meters, Locations, Finance,
// Occupancy, Holidays/term dates, Budgets, Alert rules, Data quality, Import
// readings, Users & roles, MIS import failures, Properties). Backend twin:
// ORG_ADMIN_ROLES in backend/power_backend/roles.py. The backend stays
// authoritative: active-org resolution keeps a partner admin inside their own
// partner's orgs. Billing and the other org admin pages stay ADMIN_ROLES.
export const ADMIN_OR_PARTNER_ADMIN_ROLES = [
    ...ADMIN_ROLES,
    'Partner Admin Role'
]

// Everyone who can run the org onboarding wizard: site admins and partner
// admins create orgs; org admins can re-enter to finish an incomplete setup.
export const ORG_SETUP_ROLES = [
    'Site Admin Role',
    'Partner Admin Role',
    'Admin Role'
]

// Everyone who reaches the all-members pages (Overview, Timeline, Houses, Class
// Projects, Activities, Carbon Report, Targets, Quiz, Waste). Access is
// cumulative: SLT and partner admins see everything a member sees. Every backend
// API behind these pages admits the same roles (core/tests/test_member_role_parity.py).
// Partner admins reach only orgs inside their partner (get_allowed_orgs), and
// cost visibility is a separate, narrower rule (costVisibility.js).
export const MEMBER_ROLES = [
    'Site Admin Role',
    'Partner Admin Role',
    'Admin Role',
    'Slt Role',
    'Teacher Role',
    'Staff Role',
    'Student Role'
]

// SLT-gated pages (Holidays, Explore Data, House Details). Display name
// 'Slt Role' comes from django-role-permissions: camelToSnake('SLTRole') →
// 'slt_role' → user.get_roles() does .title() → 'Slt Role'.
export const SLT_ROLES = ['Site Admin Role', 'Admin Role', 'Slt Role']

// Staff-facing roles permitted to view the alerts overview/detail (not students).
export const STAFF_VIEW_ROLES = [
    'Site Admin Role',
    'Admin Role',
    'Slt Role',
    'Staff Role',
    'Teacher Role'
]

// Quiz class mode and teacher insights: staff-facing roles plus partner admins,
// matching the backend's quiz `can_teach` check.
export const QUIZ_TEACHER_ROLES = [...STAFF_VIEW_ROLES, 'Partner Admin Role']

// The intra-org property league (Power → Properties / Sites league): the org's
// staff roles plus partner admins, read-only. Student is excluded. Backend twin:
// PROPERTY_LEAGUE_VIEW_ROLES in backend/power_backend/roles.py, which the
// property-portfolio endpoint enforces; keep the two lists identical. Cost in
// that page is the narrower canSeeCost rule (costVisibility.js).
export const PROPERTY_LEAGUE_ROLES = [...STAFF_VIEW_ROLES, 'Partner Admin Role']
/**
 * The location member role (location-scoped-member-role): a site manager, tenant or
 * housemaster who sees only the Locations granted to them. Deliberately NOT part of
 * MEMBER_ROLES or SLT_ROLES (both feed cost visibility and other routes): routes opt
 * location members in one by one with `locationScopeAware: true` plus this constant.
 */
export const LOCATION_MEMBER_ROLES = ['Location Member Role']

/** The snake_case role name, for the role pickers (`user.roleNames`). */
export const LOCATION_MEMBER_ROLE_NAME = 'location_member_role'
